import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Instagram, Facebook, Youtube, ArrowUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const Footer = () => {
  const { footerData, contactData, navigationData } = useLanguage();
  const currentYear = new Date().getFullYear();

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (href) => {
    if (window.location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialIcons = {
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube
  };

  return (
    <footer 
      className="py-16"
      style={{ background: 'var(--bg-footer)', color: 'white' }}
    >
      <div className="container">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 
              className="text-xl font-semibold mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {navigationData.logo}
            </h3>
            <p 
              className="body-small mb-6"
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {footerData.tagline}
            </p>
            <div className="flex gap-3">
              {Object.entries(contactData.social).map(([platform, handle]) => {
                const IconComponent = socialIcons[platform];
                return (
                  <a
                    key={platform}
                    href={`https://${platform}.com/${handle.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    }}
                    aria-label={platform}
                  >
                    <IconComponent size={16} />
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 
              className="text-sm font-medium mb-4 uppercase tracking-wider"
              style={{ fontFamily: 'SF Mono, monospace' }}
            >
              Links Rápidos
            </h4>
            <ul className="space-y-3">
              {footerData.quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="body-small hover:opacity-100 transition-opacity duration-200"
                    style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 
              className="text-sm font-medium mb-4 uppercase tracking-wider"
              style={{ fontFamily: 'SF Mono, monospace' }}
            >
              Navegação
            </h4>
            <ul className="space-y-3">
              {navigationData.links.slice(0, 5).map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    className="body-small hover:opacity-100 transition-opacity duration-200"
                    style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 
              className="text-sm font-medium mb-4 uppercase tracking-wider"
              style={{ fontFamily: 'SF Mono, monospace' }}
            >
              Contato
            </h4>
            <ul className="space-y-3">
              <li>
                <p 
                  className="body-small"
                  style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  {contactData.email}
                </p>
              </li>
              <li>
                <p 
                  className="body-small"
                  style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  {contactData.phone}
                </p>
              </li>
              <li>
                <p 
                  className="body-small"
                  style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  {contactData.address}
                </p>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <div 
          className="h-px mb-8"
          style={{ background: 'rgba(255, 255, 255, 0.1)' }}
        />

        {/* Bottom Row */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="caption flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1"
            style={{ color: 'rgba(255, 255, 255, 0.5)' }}
          >
            <div className="flex items-center gap-1">
              © {currentYear} {navigationData.logo}. Feito com{' '}
              <Heart size={12} style={{ color: 'var(--accent-pink-200)' }} />{' '}
              e fé.
            </div>
            <span className="hidden sm:inline mx-1">•</span>
            <a href="https://tomich.com.br" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" style={{ color: 'inherit', textDecoration: 'none' }}>
              Desenvolvido por Tomich Tecnologia
            </a>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onClick={handleScrollTop}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-200"
            style={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <span className="caption">Voltar ao topo</span>
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255, 255, 255, 0.1)' }}
            >
              <ArrowUp size={14} />
            </div>
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
